import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import AddIcon from '@material-ui/icons/Add';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import MenuItem from '@material-ui/core/MenuItem';
import { v1 as uuidv1 } from 'uuid';
import { request } from 'utilities/graph';
import {
  getVideo,
} from 'graphql/queries';
import {
  createVideo,
  updateVideo,
} from 'graphql/mutations';
import cache from 'utilities/cache';
import DataJoinEditorInput from './DataJoinEditor/DataJoinEditorInput';
import { getPropsByMode } from './DataJoinEditor/helpers';
import { videoCategories } from '@silvergatedelivery/constants';
import FileUploader from 'views/FacilityAdmin/components/Forms/FileUploader';

const mediaTypes = [
  { value: 'VIDEO', label: '影片' },
  { value: 'PDF', label: 'PDF' },
];

export default function InstructionVideoDialog({
  mode = 'add',
  category = 'tutorial',
  open: inOpen = false,
  buttonStyle = {},
  videoItem,
  buttonSize = 'medium',
  onUpdate,
}) {
  const defaultData = {
    title: '',
    description: '',
    videoUrl: '',
    category: category,
    mediaType: 'VIDEO',
  };

  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(inOpen);
  const [data, setData] = useState(defaultData);
  const [videoId, setVideoId] = useState();
  const [fileS3Key, setFileS3Key] = useState();
  const [tags, setTags] = useState([]); // tag.label string array like ["tag1", "tag2"]
  const [existedTagOptions, setExistedTagOptions] = useState([]);

  const convertVideoUrlForYoutube = (url) => {
    if (url.includes('youtube.com/watch')) {
      return `https://www.youtube.com/embed/${url.split('v=')[1]}`;
    }
    if (url.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${url.split('youtu.be/')[1]}`;
    }

    return url;
  };


  const handleSubmit = async () => {
    setIsLoading(true);
    const username = cache.get('app:username');

    const {
      title,
      description,
      videoUrl,
      category,
      mediaType,
    } = data;

    const videoData = {
      id: videoId,
      type: 'Video',
      title,
      description,
      videoUrl: mediaType === 'VIDEO' ? convertVideoUrlForYoutube(videoUrl) : fileS3Key,
      category,
      mediaType,
      ...(mode === 'add' ? { createdBy: username } : {}),
      updatedBy: username,
    };

    let savedData;
    if (mode === 'add') {
      const { data: { createVideo: result } } = await request(createVideo, { input: videoData });
      savedData = result;
    } else {
      const { data: { updateVideo: result } } = await request(updateVideo, { input: videoData });
      savedData = result;
    }

    // 處理標籤
    const {
      createQueryName,
      createJoinDataQueryName,
      deleteJoinDataQueryName,
    } = getPropsByMode('影片-教學');

    // 找出需要創建的新標籤
    const toCreateLabels = tags.filter((label) => {
      return !existedTagOptions.find(({ label: existingLabel }) => existingLabel === label);
    });

    // 創建新標籤
    const newTags = await Promise.all(toCreateLabels.map((label) => {
      return request(createQueryName, {
        input: {
          label,
          category: '影片',
          subcategory: '教學',
          createdBy: username,
          updatedBy: username,
        },
      });
    }));

    // 合併所有標籤
    const allTags = [...existedTagOptions, ...newTags.map((res) => res.data[Object.keys(res.data)[0]])];

    // 找出需要創建的關聯
    const toCreateJoinData = tags
      .filter((label) => {
        // 只保留不在 videoItem.tags.items 中的標籤
        return !videoItem?.tags?.items?.find((item) => item.tag.label === label);
      })
      .map((label) => {
        const matchedTag = allTags.find(({ label: existingLabel }) => existingLabel === label);
        return {
          id: uuidv1(),
          videoId: savedData.id,
          tagId: matchedTag.id,
          createdBy: username,
          updatedBy: username,
        };
      });

    // 找出需要刪除的關聯
    const toRemoveJoinData = videoItem?.tags?.items?.filter((item) => {
      return !tags.includes(item.tag.label);
    }).map((item) => ({ id: item.id })) || [];

    // 創建和刪除關聯
    await Promise.all([
      ...toCreateJoinData.map((item) => {
        return request(createJoinDataQueryName, { input: item });
      }),
      ...(mode !== 'add' ? toRemoveJoinData.map((item) => {
        return request(deleteJoinDataQueryName, { input: item });
      }) : []),
    ]);

    const { data: { getVideo: returnData } } = await request(getVideo, { id: savedData.id });

    onUpdate(returnData);

    setIsLoading(false);
    handleClose();
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const reset = () => {
    setVideoId(undefined);
    setTags([]);
    setData(defaultData);
    setFileS3Key(undefined);
  };

  const title = mode === 'add' ? '新增教學媒體' : '編輯教學媒體';

  useEffect(() => {
    if (!open) return;

    reset();
    if (videoItem) {
      setData({
        ...defaultData,
        ...videoItem,
      });
      setVideoId(videoItem.id);
      if (videoItem.mediaType === 'PDF') {
        setFileS3Key(videoItem.videoUrl);
      }
      if (videoItem.tags) {
        setTags(videoItem.tags.items.map((item) => item.tag.label));
      }
    } else {
      setData(defaultData);
      setVideoId(uuidv1());
    }
  }, [videoItem, open]);

  return (
    <Fragment>
      <Tooltip title={title} style={buttonStyle}>
        <IconButton
          data-testid={mode}
          aria-label={mode}
          onClick={() => setOpen(true)}
          size={buttonSize}
        >
          {mode === 'add' ? <AddIcon /> : <EditIcon />}
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === 'escapeKeyDown' || reason === 'backdropClick') return;
          handleClose();
        }}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          {title}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="title"
            label="標題"
            type="text"
            value={data.title}
            fullWidth
            required
            onChange={(e) => setData({ ...data, title: e.target.value })}
          />
          <TextField
            margin="dense"
            id="description"
            label="描述"
            type="text"
            value={data.description}
            fullWidth
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
          <TextField
            select
            margin="dense"
            id="mediaType"
            label="媒體類型"
            value={data.mediaType}
            fullWidth
            required
            onChange={(e) => setData({ ...data, mediaType: e.target.value, videoUrl: '' })}
          >
            {mediaTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          { data.mediaType === 'VIDEO' ?
            <TextField
              margin="dense"
              id="videoUrl"
              label="影片URL"
              type="text"
              value={data.videoUrl}
              fullWidth
              required
              onChange={(e) => setData({ ...data, videoUrl: e.target.value })}
              helperText="例如: https://scribehow.com/embed/__i47bUzwZR4CqPO2j8wePug?as=video 或 https://www.youtube.com/watch?v=UvB8uOEP18k"
            /> :
            <FileUploader
              s3Prefix={`instruction-media/${videoId}`}
              s3Key={fileS3Key}
              onUpdateS3Key={setFileS3Key}
            />
          }
          <TextField
            select
            margin="dense"
            id="category"
            label="分類"
            value={data.category}
            fullWidth
            required
            onChange={(e) => setData({ ...data, category: e.target.value })}
          >
            {videoCategories.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <DataJoinEditorInput
            title="標籤"
            mode="影片-教學"
            defaultValues={videoItem?.tags?.items?.map((item) => item.tag.label) || []}
            onChange={(newTags) => {
              setTags(newTags.map((tag) => tag.label));
            }}
            onUpdateOptions={setExistedTagOptions}
            showHelperText={false}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" disabled={isLoading}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading || !data.title || !(data.mediaType === 'VIDEO' ? data.videoUrl : fileS3Key) || !data.category}
          >
            儲存
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

InstructionVideoDialog.propTypes = {
  mode: PropTypes.string,
  category: PropTypes.string,
  open: PropTypes.bool,
  buttonStyle: PropTypes.object,
  videoItem: PropTypes.object,
  buttonSize: PropTypes.string,
  onUpdate: PropTypes.func,
};
